/**
 * Shared project-search board for the investment hub (Bucket B opt 1 & 2).
 * `variant='invest'` = licensed projects with a sale/rent toggle + the current
 * investors strip; `variant='contribute'` = projects seeking development funding,
 * with the developer-set contribution range. Both reuse the city filter + grid.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import DistrictSearch from '../../components/search/DistrictSearch';
import ProjectInvestmentCard from './ProjectInvestmentCard';
import { fetchPublicProjects, type PublicProject } from '../../services/projectService';
import type { AreaSuggestion } from '../../services/neighborhoodMapService';

const PAGE_SIZE = 12;

const CITIES = [
  { value: '', en: 'All cities', ar: 'كل المدن' },
  { value: 'Riyadh', en: 'Riyadh', ar: 'الرياض' },
  { value: 'Jeddah', en: 'Jeddah', ar: 'جدة' },
  { value: 'Makkah', en: 'Makkah', ar: 'مكة المكرمة' },
  { value: 'Madinah', en: 'Madinah', ar: 'المدينة المنورة' },
  { value: 'Dammam', en: 'Dammam', ar: 'الدمام' },
];

const LISTING_TOGGLE: { value: '' | 'SALE' | 'RENT'; en: string; ar: string }[] = [
  { value: '', en: 'All', ar: 'الكل' },
  { value: 'SALE', en: 'For sale', ar: 'للبيع' },
  { value: 'RENT', en: 'For rent', ar: 'للإيجار' },
];

interface BoardProps {
  variant: 'invest' | 'contribute';
  titleEn: string;
  titleAr: string;
  subtitleEn: string;
  subtitleAr: string;
  icon: string;
}

export default function ProjectInvestmentBoard({ variant, titleEn, titleAr, subtitleEn, subtitleAr, icon }: BoardProps) {
  const { i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';

  const [city, setCity] = useState('');
  const [area, setArea] = useState<AreaSuggestion | null>(null);
  const [listingType, setListingType] = useState<'' | 'SALE' | 'RENT'>('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<PublicProject[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => { setPage(1); }, [city, area, listingType]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchPublicProjects({
      city: city || undefined,
      areaId: area?.id,
      listingType: variant === 'invest' && listingType ? listingType : undefined,
      seekingContribution: variant === 'contribute',
      page,
      pageSize: PAGE_SIZE,
    })
      .then((res) => { if (cancelled) return; setItems(res.data); setTotalPages(res.meta.totalPages || 1); })
      .catch(() => { if (cancelled) return; setItems([]); setTotalPages(1); setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [variant, city, area, listingType, page]);

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) { setPage(next); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main id="main-content" className="pb-20">
        <section className="bg-[#003930] py-12 text-white">
          <div className="mx-auto max-w-6xl px-4 text-center">
            <Link to="/investors" className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/80 hover:text-white">
              <i className={isRTL ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} aria-hidden="true" />{isRTL ? 'منصة الاستثمار' : 'Investment hub'}
            </Link>
            <h1 className="font-display text-3xl font-bold sm:text-4xl"><i className={`${icon} mx-2`} aria-hidden="true" />{isRTL ? titleAr : titleEn}</h1>
            <p className="mt-3 text-sm text-white/85 sm:text-base">{isRTL ? subtitleAr : subtitleEn}</p>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-6">
          {/* Filters */}
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap gap-1.5">
              {CITIES.map((c) => (
                <button key={c.value || 'all'} type="button" onClick={() => setCity(c.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${city === c.value ? 'bg-[#0D201C] text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                  {isRTL ? c.ar : c.en}
                </button>
              ))}
            </div>
            {variant === 'invest' && (
              <div className="flex gap-1.5">
                {LISTING_TOGGLE.map((o) => (
                  <button key={o.value || 'all'} type="button" onClick={() => setListingType(o.value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${listingType === o.value ? 'bg-[#004D40] text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                    {isRTL ? o.ar : o.en}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* District search — narrow to a neighborhood (e.g. south Jeddah). */}
          <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center">
            <DistrictSearch
              onSelect={setArea}
              className="w-full sm:max-w-xs"
              placeholder={isRTL ? 'ابحث عن حي (مثال: جنوب جدة)…' : 'Search a district (e.g. south Jeddah)…'}
            />
            {area && (
              <button
                type="button"
                onClick={() => setArea(null)}
                className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[var(--pv-color-surface-sunken)] px-3 py-1.5 text-xs font-medium text-[#003930] hover:bg-[#DDEEEB]"
              >
                <i className="ri-map-pin-2-fill" aria-hidden="true" />
                {isRTL ? (area.nameAr || area.nameEn) : area.nameEn}
                <i className="ri-close-line" aria-hidden="true" />
              </button>
            )}
          </div>

          {/* Results */}
          {loading ? (
            <div className="flex justify-center py-20"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]"><i className={`${icon} text-2xl text-[#004D40]`} aria-hidden="true" /></div>
              <p className="font-display text-lg font-semibold text-slate-800">
                {error ? (isRTL ? 'تعذّر تحميل المشاريع' : 'Could not load projects') : (isRTL ? 'لا توجد مشاريع بعد' : 'No projects yet')}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                {error ? (isRTL ? 'حاول مرة أخرى لاحقاً.' : 'Please try again later.')
                  : (isRTL ? 'ستظهر المشاريع المرخّصة في هذه المدينة قريباً.' : 'Licensed projects in this city will appear here soon.')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((p) => <ProjectInvestmentCard key={p.id} project={p} isRTL={isRTL} variant={variant} />)}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className={`mt-8 flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">{isRTL ? 'السابق' : 'Prev'}</button>
              <span className="px-2 text-sm text-slate-500">{isRTL ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}</span>
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">{isRTL ? 'التالي' : 'Next'}</button>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
